import { Checkbox, CheckboxProps, FormControlLabel, Stack, Typography } from '@mui/material'
import ConfirmModal from 'components/modals/ConfirmModal'
import { useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

type Props = {
  name: string
  label: string
  confirmText?: string
  warningText?: string
  confirmChangeToTrue?: boolean
} & CheckboxProps

const FormCheckbox = ({
  name,
  label,
  confirmText,
  warningText,
  confirmChangeToTrue,
  ...rest
}: Props) => {
  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext()

  const [confirmState, setConfirmState] = useState({
    open: false,
    confirmFn: () => undefined,
  })

  const handleClose = () => {
    setConfirmState((state) => ({ ...state, open: false }))
  }

  const handleConfirm = () => {
    confirmState.confirmFn()
    setConfirmState((state) => ({ ...state, open: false, confirmFn: () => undefined }))
  }

  const handleChange = (value: boolean, onChange: (v: boolean) => void) => {
    const shouldConfirm = confirmChangeToTrue ? value : !value
    if (!confirmText || !shouldConfirm) {
      onChange(value)
      return
    }
    setConfirmState((state) => ({
      ...state,
      open: true,
      confirmFn: () => {
        onChange(value)
        return undefined
      },
    }))
  }

  return (
    <>
      <Stack>
        <FormControlLabel
          control={
            <Controller
              control={control}
              name={name}
              render={({ field }) => (
                <Checkbox
                  name={name}
                  checked={field.value}
                  color="primary"
                  onChange={(e) => handleChange(e.target.checked, field.onChange)}
                  {...rest}
                />
              )}
            />
          }
          label={label}
        />
        {errors[name] ? (
          <Typography variant="body2" sx={{ fontSize: '0.75rem' }} color="error">
            {(errors[name] as { message?: string })?.message || ''}
          </Typography>
        ) : null}
        {warningText && watch(name) ? (
          <Typography
            variant="body2"
            sx={{ fontSize: '0.75rem', whiteSpace: 'pre-wrap' }}
            color="secondary"
          >
            {warningText || ''}
          </Typography>
        ) : null}
      </Stack>
      {confirmState.open && (
        <ConfirmModal
          title=""
          loading={false}
          body={confirmText}
          handleClose={handleClose}
          handleConfirm={handleConfirm}
        />
      )}
    </>
  )
}

export default FormCheckbox
